import React, { useState, useEffect } from 'react';
import { toast } from '../Toast';
import { askConfirm } from '../ui';
import {
  MessageSquare, Pin, Send, Reply, Trash2, Edit3,
  ChevronDown, ChevronUp, Loader2, MoreVertical, X,
} from 'lucide-react';
import { commApi } from '../../services/crmApi';

interface Comment {
  id: number;
  application_id: number;
  author_id: number;
  author_name: string;
  content: string;
  is_pinned: boolean;
  parent_id: number | null;
  mentions: any[];
  created_at: string;
  updated_at: string;
}

export default function CommentsSection({ applicationId }: { applicationId: number }) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [menuOpen, setMenuOpen] = useState<number | null>(null);

  const loadComments = async () => {
    try {
      const data = await commApi.comments(applicationId);
      setComments(data);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { loadComments(); }, [applicationId]);

  const handleSubmit = async () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      await commApi.addComment(applicationId, {
        content: newComment.trim(),
        parent_id: replyTo || undefined,
      });
      setNewComment('');
      setReplyTo(null);
      loadComments();
    } catch { toast.error('Błąd dodawania komentarza'); }
    setSubmitting(false);
  };

  const handlePin = async (id: number, pinned: boolean) => {
    await commApi.updateComment(id, { is_pinned: !pinned });
    setMenuOpen(null);
    loadComments();
  };

  const handleDelete = async (id: number) => {
    if (!(await askConfirm({ title: 'Usuńac komentarz?' }))) return;
    await commApi.deleteComment(id);
    setMenuOpen(null);
    loadComments();
  };

  const handleEdit = async (id: number) => {
    if (!editContent.trim()) return;
    await commApi.updateComment(id, { content: editContent.trim() });
    setEditingId(null);
    setEditContent('');
    loadComments();
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 60000) return 'przed chwila';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min temu`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h temu`;
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Organize: pinned first, then thread structure
  const pinned = comments.filter(c => c.is_pinned && !c.parent_id);
  const topLevel = comments.filter(c => !c.is_pinned && !c.parent_id);
  const replies = comments.filter(c => c.parent_id);

  const getReplies = (parentId: number) => replies.filter(r => r.parent_id === parentId);

  const renderComment = (comment: Comment, isReply = false) => (
    <div key={comment.id} className={`group ${isReply ? 'ml-8 border-l-2 border-gray-200 pl-3' : ''}`}>
      <div className={`flex items-start gap-2.5 py-2.5 ${comment.is_pinned ? 'bg-yellow-50/50 -mx-2 px-2 rounded-lg' : ''}`}>
        {/* Avatar */}
        <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
          {comment.author_name?.charAt(0)?.toUpperCase() || '?'}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-800">{comment.author_name}</span>
            <span className="text-[10px] text-gray-400">{formatDate(comment.created_at)}</span>
            {comment.is_pinned && <Pin size={10} className="text-yellow-500" />}
          </div>

          {editingId === comment.id ? (
            <div className="mt-1 flex gap-1">
              <textarea
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                className="flex-1 text-xs border rounded px-2 py-1 resize-none"
                rows={2}
                autoFocus
              />
              <button onClick={() => handleEdit(comment.id)} className="text-blue-600 text-xs font-medium px-2">Zapisz</button>
              <button onClick={() => setEditingId(null)} className="text-gray-400 text-xs px-1">Anuluj</button>
            </div>
          ) : (
            <p className="text-xs text-gray-700 mt-0.5 whitespace-pre-wrap leading-relaxed">{comment.content}</p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => { setReplyTo(comment.id); }}
              className="text-[10px] text-gray-400 hover:text-blue-600 flex items-center gap-0.5"
            >
              <Reply size={10} /> Odpowiedz
            </button>
          </div>
        </div>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen(menuOpen === comment.id ? null : comment.id)}
            className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical size={12} className="text-gray-400" />
          </button>
          {menuOpen === comment.id && (
            <div className="absolute right-0 top-6 bg-white border rounded-lg shadow-lg py-1 z-10 min-w-[130px]">
              <button onClick={() => handlePin(comment.id, comment.is_pinned)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2">
                <Pin size={12} /> {comment.is_pinned ? 'Odepnij' : 'Przypnij'}
              </button>
              <button onClick={() => { setEditingId(comment.id); setEditContent(comment.content); setMenuOpen(null); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2">
                <Edit3 size={12} /> Edytuj
              </button>
              <button onClick={() => handleDelete(comment.id)} className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2">
                <Trash2 size={12} /> Usuń
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      {getReplies(comment.id).map(r => renderComment(r, true))}
    </div>
  );

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-blue-400" /></div>;

  return (
    <div>
      {/* Pinned comments */}
      {pinned.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] font-semibold text-yellow-600 uppercase mb-1 flex items-center gap-1">
            <Pin size={10} /> Przypinane
          </div>
          {pinned.map(c => renderComment(c))}
        </div>
      )}

      {/* Comments list */}
      <div className="divide-y divide-gray-50">
        {topLevel.map(c => renderComment(c))}
      </div>

      {topLevel.length === 0 && pinned.length === 0 && (
        <div className="text-center py-6 text-gray-400">
          <MessageSquare size={24} className="mx-auto mb-2 opacity-30" />
          <p className="text-xs">Brak komentarzy</p>
        </div>
      )}

      {/* Reply indicator */}
      {replyTo && (
        <div className="mt-2 flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-2 py-1 rounded">
          <Reply size={10} /> Odpowiedz na komentarz #{replyTo}
          <button onClick={() => setReplyTo(null)} className="ml-auto"><X size={10} /></button>
        </div>
      )}

      {/* New comment input */}
      <div className="mt-3 flex gap-2 items-end">
        <textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder="Dodaj komentarz lub notatke..."
          rows={2}
          className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-2 resize-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 placeholder:text-gray-400"
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit();
          }}
        />
        <button
          onClick={handleSubmit}
          disabled={!newComment.trim() || submitting}
          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed shrink-0"
          title="Wyślij (Ctrl+Enter)"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>
    </div>
  );
}
